import { DocumentSvcCrunches } from '@dc-shared-web/hooks/api/document/types'
import { AxiosInstance } from 'axios'

type Assignment = {
  type: 'checklist' | 'playbook'
  uuid: string
  userUuid: string
}

/**
 * Upload a new document.
 * Either as a version of an existing crunch or as a new crunch.
 * @param file - The file to upload (must be PDF, DOC, or DOCX format)
 * @param templateUuid - origin_uuid from ToCData
 * @param crunchUuid - required for a version upload. If left off, then a new crunch will be created.
 * @param officeUuid - UUID of the office/team
 * @param checklists - Array of checklist UUIDs to apply
 * @param playbookUuid - playbook UUID to apply
 * @param assignments - Array of assignments with type, uuid, and userUuid
 * @param projectUuid - UUID of the project to associate with
 * @param folderUuid - UUID of the folder to place document in
 * @param documentTypeUuid - UUID of the document type
 **/
export type UploadDocumentParams = {
  file: File
  templateUuid?: string
  crunchUuid?: string
  officeUuid?: string
  checklists?: string[]
  playbookUuid?: string
  assignments?: Assignment[]
  projectUuid?: string
  folderUuid?: string
  documentTypeUuid?: string
}

export type UploadDocumentResponse = {
  message: string
  crunch: DocumentSvcCrunches
}

export const uploadDocument = async (
  apiClient: AxiosInstance,
  params: UploadDocumentParams
): Promise<UploadDocumentResponse> => {
  const formData = new FormData()

  const { assignments, checklists, ...stringifiedParams } = params

  /* We append the stringified params to the form data. */
  Object.entries(stringifiedParams).forEach(([key, value]) => {
    formData.append(key, value)
  })

  if (assignments) {
    formData.append('assignments', JSON.stringify(assignments))
  }

  if (checklists) {
    formData.append('checklists', JSON.stringify(checklists))
  }

  const { data } = await apiClient.post(`/api/crunches/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return data
}
